import React, { useMemo, useState } from "react";
import { IVideo } from "../hooks/useFetchVideos";
import { getAutoGenThumbnail, formatWebOptimized, formatWebM } from "./helpers";
import { fancyTimeFormat } from "../adminVideosV2/helper";
import { formatFps } from "../../../../components/videoSection/videoDetails";
import { IconAlertGlyph } from "./panels";

/**
 * The player plate.
 *
 * The plate's size is decided from `aspect_ratio` BEFORE the file loads, so
 * nothing reflows when the intrinsic size arrives. Previously the video sat
 * between two full-width rules with `maxHeight: calc(100vh - 400px)` and no
 * reserved box at all.
 */

const LANDSCAPE = { w: 560, h: 315 };
/** Nested inside the expert / product page the module is narrower (§04). */
const LANDSCAPE_COMPACT = { w: 440, h: 248 };

/**
 * Portrait video gets a PORTRAIT plate rather than being pillarboxed into a
 * landscape one. The handoff specified a fixed 560x315 for every ratio, which
 * renders a 9x16 at ~177px wide — narrower than it is today, on a page whose
 * primary job is watching, for a format that is a large share of the library.
 * Set `PORTRAIT_PLATE = false` to get the handoff's fixed plate back.
 */
const PORTRAIT_PLATE = true;
const PORTRAIT = { w: 420, h: 560 };
const PORTRAIT_COMPACT = { w: 330, h: 440 };
/** Square gets its own box too, rather than pillarboxing into the landscape one. */
const SQUARE = { w: 420, h: 420 };
const SQUARE_COMPACT = { w: 330, h: 330 };

/** "9x16" / "9:16" / "16X9" -> ratio. Falls back to landscape. */
const parseAspect = (raw?: string): number => {
  const m = String(raw || "").match(/(\d+(?:\.\d+)?)\s*[xX:]\s*(\d+(?:\.\d+)?)/);
  if (!m) return 16 / 9;
  const w = Number(m[1]);
  const h = Number(m[2]);
  return w > 0 && h > 0 ? w / h : 16 / 9;
};

interface Props {
  video: IVideo;
  /** Nested host: the module is narrower, so the plate steps down. */
  compact?: boolean;
}

/**
 * The plate for a given aspect string. Exported so the page skeleton reserves
 * the SAME box the player will occupy — it used to hardcode 560x315, so every
 * portrait video snapped from landscape to 420x560 the moment it loaded, which
 * undid the aspect-reserved plate for exactly the format it was built for.
 */
export const plateFor = (raw?: string, compact = false) => {
  const ratio = parseAspect(raw);
  if (!PORTRAIT_PLATE) return compact ? LANDSCAPE_COMPACT : LANDSCAPE;
  if (ratio < 0.98) return compact ? PORTRAIT_COMPACT : PORTRAIT;
  if (ratio < 1.02) return compact ? SQUARE_COMPACT : SQUARE;
  return compact ? LANDSCAPE_COMPACT : LANDSCAPE;
};

export function PanelVideo({ video, compact = false }: Props) {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Remounts the media element so every <source> is attempted again.
  const [attempt, setAttempt] = useState(0);

  const sourceUrl = video.location || video.reviewS3File?.location || "";
  const plate = plateFor(video.aspect_ratio, compact);

  // Plain `_thumbnail.png`, never `_thumbnail_play_icon.png` — that derivative
  // bakes a play triangle into the image, which then sits under the browser's
  // own play button.
  const posterUrl = useMemo(
    () => (sourceUrl ? getAutoGenThumbnail(sourceUrl, ".png") : ""),
    [sourceUrl],
  );

  // Smallest first, with real fallbacks. The media element only fires `error`
  // once every source has failed.
  const sources = useMemo(() => {
    if (!sourceUrl) return [];
    const webm = formatWebM(video).url;
    const optimized = formatWebOptimized(video).url;
    const ext = (u: string) => u.substring(u.lastIndexOf(".") + 1) || "mp4";
    return [
      webm ? { src: webm, type: "video/webm" } : null,
      optimized && optimized !== sourceUrl
        ? { src: optimized, type: `video/${ext(optimized)}` }
        : null,
      { src: sourceUrl, type: `video/${ext(sourceUrl)}` },
    ].filter(Boolean) as Array<{ src: string; type: string }>;
  }, [video, sourceUrl]);

  const duration = video?.length ? fancyTimeFormat(video.length) : "";

  // With no source at all the media element parks at NETWORK_EMPTY and fires
  // neither `canplay` nor `error`, so `loading` stayed true forever and the plate
  // sat on a permanent shimmer with no message and no way out — while this
  // component's own failure copy was the state that should have shown.
  const unplayable = failed || sources.length === 0;

  return (
    <div
      className="relative rounded-xl overflow-hidden shrink-0 w-full"
      style={{
        maxWidth: plate.w,
        aspectRatio: `${plate.w} / ${plate.h}`,
        background: "var(--player)",
        boxShadow: "var(--sh-1)",
      }}
    >
      {unplayable ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-center px-6">
          <span style={{ color: "var(--rail-text-2, #A9B2C1)" }}>
            <IconAlertGlyph size={24} strokeWidth={1.6} />
          </span>
          <div className="font-CircularStdBlack" style={{ fontSize: 14, color: "#EAEDF2" }}>
            Can&apos;t play this file
          </div>
          <p className="m-0" style={{ fontSize: 12.5, color: "#A9B2C1" }}>
            The web version is missing or unsupported.
          </p>
          {/* Nothing to retry when there was never a URL. */}
          {sources.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setFailed(false);
                setLoading(true);
                setAttempt((n) => n + 1);
              }}
              className="mt-1.5 inline-flex items-center h-8 px-3.5 rounded-lg"
              style={{ background: "rgba(255,255,255,.12)", color: "#FFFFFF", fontSize: 12.5 }}
            >
              Retry
            </button>
          )}
          <p className="m-0 mt-0.5" style={{ fontSize: 11, color: "#6E7889" }}>
            Download still works — the original file is unaffected.
          </p>
        </div>
      ) : (
        <>
          {loading && (
            // Shimmer at the final plate size — no Lottie, no layout shift.
            <div className="absolute inset-0 animate-pulse motion-reduce:animate-none" style={{ background: "var(--player)" }} />
          )}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            key={attempt}
            controls
            playsInline
            preload="metadata"
            poster={posterUrl}
            // `canplay` alone is not enough: a browser that honours
            // preload="metadata" strictly stops at HAVE_METADATA and never fires
            // it, and the element is display:none until then, so the user cannot
            // reach the native control to force the load.
            onLoadedMetadata={() => setLoading(false)}
            onLoadedData={() => setLoading(false)}
            onCanPlay={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setFailed(true);
            }}
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "contain", display: loading ? "none" : "block" }}
          >
            {sources.map((s) => (
              <source key={s.src} src={s.src} type={s.type} />
            ))}
          </video>

          {/* Chips ride the plate, on --scrim, never a gradient. */}
          {duration && (
            <span
              className="absolute left-3 bottom-3 inline-flex items-center h-6 px-2.5 rounded-full text-white tabular-nums pointer-events-none"
              style={{ background: "var(--scrim)", fontSize: 11.5 }}
            >
              {duration}
            </span>
          )}
          {video.aspect_ratio && (
            <span
              className="absolute right-3 bottom-3 inline-flex items-center h-6 px-2.5 rounded-full text-white font-CircularStdBlack uppercase pointer-events-none"
              style={{ background: "var(--scrim)", fontSize: 10, letterSpacing: ".1em" }}
            >
              {video.aspect_ratio}
              {formatFps(video.fps) ? ` · ${formatFps(video.fps)}` : ""}
            </span>
          )}
        </>
      )}
    </div>
  );
}
